import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import tw, { theme } from 'twin.macro'
import { PlusIcon, LockIcon } from '@components/Icons'
import ImageWithFallback from '@components/ImageWithFallback'
import appUrlLib, { imageUrl } from '@libs/appUrl'
import { getIndexUserCollectionList } from '@app-core/profile/actions'
import Loading from '@components/Loading'
import { trackUserProfileCollectionListEnterCollectionList } from '@libs/eventTracking/profile/user-profile.collection-list.enter-collection-list'

const LinkWithRnAppWebview = ({ children, ...props }) => {
  const handleOnClick = (e) => {
    e.preventDefault()
    if (window.RNApp && window.RNApp.webview) {
      window.RNApp.webview(props.href)
    } else {
      window.open(props.href)
    }
  }

  return (
    <a {...props} onClick={handleOnClick}>
      {children}
    </a>
  )
}

const UserCollectionsContainer = styled.div`
  ${tw`text-h5`}
  margin: 0 3px;
  margin-top: 6px;
`

const LoadingContainer = styled.div`
  padding: 40px 0;
`

const IntersectionObserverTarget = styled.div`
  position: absolute;
  bottom: 0px;
  right: 0px;
  width: 100px;
  height: 50vh;
  z-index: -100000;
  border: 1px solid black;
`

const UserCollectionRow = styled.a`
  padding: 10px 10px;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 8px;
  @media (min-width: ${theme`screens.md`}) {
    padding: 21px 10px;
    &:hover {
      ${tw`bg-primary-light`}
    }
  }
`

const PlusIconContainer = styled.div`
  ${tw`border border-primary-300 bg-primary-light`}
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  margin-right: 12px;
`

const StyledPlusIcon = styled(PlusIcon)`
  ${tw`text-primary-indigo`}
  display: bloack;
  width: 24px;
  height: 24px;
`

const UserCollectionTitle = styled.div`
  margin-right: 8px;
`

const UserCollectionImage = styled(ImageWithFallback)`
  display: block;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  margin-right: 12px;
  object-fit: cover;
`

const StatusHintContainer = styled.div`
  margin-top: 38px;
  text-align: center;
  @media (min-width: ${theme`screens.md`}) {
    margin-top: 60px;
  }
`

const StatusHintImage = styled.img`
  width: 180px;
  height: 180px;
  margin: 0 auto;
`

const StatusHintText = styled.div`
  ${tw`text-h5 text-black font-normal`}
  margin: 0 auto;
  margin-top: 20px;
  margin-bottom: 32px;
  line-height: 24px;
`

interface StatusHintButtonProps {
  isprimary?: boolean
}

const StatusHintButton = styled(LinkWithRnAppWebview)<StatusHintButtonProps>`
  display: block;
  ${tw`text-h5 font-medium bg-primary-indigo`}
  ${(props: any) =>
    props.isprimary ? tw`text-white bg-primary-indigo` : tw`text-black bg-gray-30`}
  margin: 0 auto;
  height: 56px;
  line-height: 56px;
  text-align: center;
  width: 240px;
  border-radius: 28px;
`

const UserCollections = () => {
  const dispatch = useDispatch()
  const profileCollections = useSelector<any, any>((state) =>
    state.profile.getIn(['userCollections']),
  )
  const isGuest = useSelector<any, any>((state) => state.profile.getIn(['isGuest']))
  const isMeRoute = useSelector<any, any>((state) => state.profile.getIn(['isMeRoute']))

  const intersectionObserverTargetRef = useRef(null)
  const currentCollections = useRef<any>(null)
  const collections = profileCollections.getIn(['list']).toJS()
  const isLoading = profileCollections.getIn(['isLoading'], false)

  const fetchMore = () => {
    if (!currentCollections.current) return
    const hasMore = currentCollections.current.getIn(['hasMore'], true)
    const isLoading = currentCollections.current.getIn(['isLoading'], false)
    if (isLoading || !hasMore) return

    const type = currentCollections.current.getIn(['type'])
    const clas = currentCollections.current.getIn(['clas'])
    const mode = currentCollections.current.getIn(['mode'])
    const page = currentCollections.current.getIn(['page'])
    const score = currentCollections.current.getIn(['score'])
    const touch = currentCollections.current.getIn(['currentList']).toJS()
    dispatch(getIndexUserCollectionList({ type, clas, mode, page, score, touch }))
  }

  const handleCreateIntersectionObserver = () => {
    if (intersectionObserverTargetRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (!entry.isIntersecting) {
            return
          }
          fetchMore()
        },
        {
          threshold: [0, 0.25, 0.5, 0.75, 1],
        },
      )
      observer.observe(intersectionObserverTargetRef.current)
      return observer
    }
  }

  const handleCollectionOnClick = (e, postID: string) => {
    e.preventDefault()
    const url = `${appUrlLib.appUrl}/${postID.replace('.', '/')}`

    trackUserProfileCollectionListEnterCollectionList({
      toPage: {
        pageUrl: url,
        pageName: 'collection-list',
      },
      postID,
    })

    if (window.RNApp && window.RNApp.webview) {
      window.RNApp.webview(url)
    } else {
      window.open(url)
    }
  }

  const handleCreateCollectionOnClick = (e) => {
    e.preventDefault()
    const url = `${appUrlLib.appUrl}/create-topic`
    if (window.RNApp && window.RNApp.webview) {
      window.RNApp.webview(url)
    } else {
      window.open(url)
    }
  }

  useEffect(() => {
    const intersectionObserver = handleCreateIntersectionObserver()
    return () => intersectionObserver.disconnect()
  }, [])

  useEffect(() => {
    // 隨時更新最新的 collections 到 currentCollections，讓 fetchMore 可以使用
    currentCollections.current = profileCollections
  })

  if (isMeRoute && isGuest) {
    return (
      <StatusHintContainer>
        <StatusHintImage src="/statics/images/profile/Prompt_App.svg" />
        <StatusHintText>
          <div>你尚未登入喔！</div>
          <div>立即加入波波宇宙</div>
        </StatusHintText>
        <StatusHintButton isprimary href={`${appUrlLib.appUrl}/login`}>
          註冊／登入
        </StatusHintButton>
      </StatusHintContainer>
    )
  }

  if (!isMeRoute && !collections.length) {
    return (
      <StatusHintContainer>
        <StatusHintImage src="/statics/images/profile/Prompt_Welcome.svg" />
        <StatusHintText>
          <div>尚無收藏</div>
        </StatusHintText>
      </StatusHintContainer>
    )
  }

  return (
    <div tw="relative">
      <UserCollectionsContainer>
        {isMeRoute && (
          <UserCollectionRow
            href={`${appUrlLib.appUrl}/create-topic`}
            onClick={handleCreateCollectionOnClick}
          >
            <PlusIconContainer>
              <StyledPlusIcon />
            </PlusIconContainer>
            <div>新增收藏清單</div>
          </UserCollectionRow>
        )}
        {collections.map((collection) => {
          const {
            _id,
            postID,
            topic: { title, cover, privacy },
          } = collection
          const fallbackCover = '/images/articleCardImagePlaceholder.svg'

          return (
            <UserCollectionRow
              key={_id}
              href={`${appUrlLib.appUrl}/${postID.replace('.', '/')}`}
              onClick={(e) => handleCollectionOnClick(e, postID)}
            >
              <UserCollectionImage
                src={cover ? imageUrl(cover) : fallbackCover}
                fallbackSrc={fallbackCover}
              />
              <UserCollectionTitle>{title}</UserCollectionTitle>
              {!!privacy && <LockIcon />}
            </UserCollectionRow>
          )
        })}
        <IntersectionObserverTarget ref={intersectionObserverTargetRef} />
        {isLoading && (
          <LoadingContainer>
            <Loading />
          </LoadingContainer>
        )}
      </UserCollectionsContainer>
    </div>
  )
}

export default UserCollections
